import { orderHsitoryTypeConstants } from "constants/orderHistory";
import { ToastContext } from "context/ToastContext";
import { useUserWithAccount } from "context/UserAccountProvider";
import { parseDate, parseTime } from "helpers/date";
import { centsToDollars, parsePrice, dollarsToCents } from "helpers/price";
import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Timeline } from "primereact/timeline";
import { useMemo, useState, useContext } from "react";
import { useCreateOrderHistoryMutation } from "store/queries/orderHistory";
import styled from "styled-components";
import { IOrderHistory } from "types/Order/Order";

import { AddNoteDialog, IPaymentNoteData } from "./components/AddNoteDialog";

const StyledCard = styled(Card)`
  margin-bottom: ${sizer(2)};
  border: 1px solid var(--gray-new-200) !important;
`;

const StyledTimeline = styled(Timeline)`
  .p-timeline-event-opposite {
    flex: 0 0;
    min-width: 178px;
    padding-top: 6px !important;
  }
  .p-timeline-event-content {
    padding-top: 6px !important;
  }
`;

const StyledShowMore = styled.p`
  color: var(--color-purple);
  margin-top: 30px;
`;

const descriptionRenderer: { [key: string]: string } = {
  Message1: "Message 1",
  Message2: "Message 2",
  Message3: "Message 3",
  Receipt: "Receipt",
};

const generateOrderHistoryLine = (item: IOrderHistory, patientName: string) => {
  const isPaid = !!(item.type === "Paid");
  const isPartiallyPaid = !!(item.type === "PartiallyPaid");
  const isInstallmentPaid = !!(item.type === "InstallmentPaid");
  const isPaidAdditionalAmount = !!(
    item.type === orderHsitoryTypeConstants.PaidAdditionalAmount
  );
  const isExternalPaymentRecorded = !!(
    item.type === orderHsitoryTypeConstants.ExternalPaymentRecorded
  );
  const isCommunicationSent = !!(
    item.type === "EmailSent" ||
    item.type === "TextMessageSent" ||
    item.type === "EmailResent" ||
    item.type === "TextMessageResent"
  );
  const isTransfer = item.type === "Transfer";
  const isBalanceUpdated = item.type === "BalanceUpdated";
  const isBalanceReported = item.type === "BalanceReported";
  const isProviderPriorityUpdated = item.type === "ProviderPriorityUpdated";
  const isTransferBlocked = !!(
    item.type === "PaymentMarkedBlockedForTransfers"
  );
  const isTransferReady = !!(item.type === "PaymentMarkedReadyForTransfers");
  const isUpdated = item.type === "Updated";

  const toName =
    item.toProvider?.name ||
    (item.toPatient?.firstName && item.toPatient?.lastName
      ? `${item.toPatient.firstName} ${item.toPatient.lastName}`
      : "Clear Health");
  const transferText = ` ${parsePrice(
    centsToDollars(item.amountInCents || 0)
  )} From ${item.fromProvider?.name || "Clear Health"} To ${toName}`;
  return (
    <div>
      {parseDate(item.createdAt, false, true)} {parseTime(item.createdAt, true)}{" "}
      {isTransfer && transferText}
      {(isPartiallyPaid ||
        isInstallmentPaid ||
        isPaidAdditionalAmount ||
        isExternalPaymentRecorded) &&
        item.amountInCents !== null &&
        item.amountInCents !== undefined &&
        ` Amount ${parsePrice(centsToDollars(item.amountInCents))}`}{" "}
      {isExternalPaymentRecorded &&
        ` Payment Type: ${item.externalPaymentMethod}`}{" "}
      {item.user &&
        !isPaid &&
        !isPartiallyPaid &&
        !isPaidAdditionalAmount &&
        `by: ${item.user.name}`}
      {item.user &&
        (isPaid || isPartiallyPaid || isPaidAdditionalAmount) &&
        ` Collected by ${item.user.name}`}{" "}
      {isBalanceReported && item.user && ` Reported by ${item.user.name}`}{" "}
      {isBalanceReported && !item.user && ` Reported by ${patientName}`}{" "}
      {item.thirdParty && `by ${item.thirdParty}`}{" "}
      {isCommunicationSent && item.description
        ? descriptionRenderer[item.description]
        : ""}
      {isUpdated && item.description && `, ${item.description}`}
      {isBalanceUpdated && item.description && (
        <span className="-ml-1">, {item.description}</span>
      )}
      {isBalanceUpdated &&
        item.providerValueChanges?.length &&
        item.providerValueChanges.map((item, index) => (
          <div key={`${item.providerId}_${index}`} className="mt-1">
            {item?.provider?.name} - from{" "}
            {parsePrice(centsToDollars(item.initialValue))} to{" "}
            {parsePrice(centsToDollars(item.newValue))}
            {item.initialPriority !== undefined &&
              item.newPriority !== undefined && (
                <span>
                  , priority changed from {item.initialPriority} to{" "}
                  {item.newPriority}
                </span>
              )}
          </div>
        ))}
      {isProviderPriorityUpdated && item.description && `, ${item.description}`}
      {isProviderPriorityUpdated &&
        item.providerValueChanges?.length &&
        item.providerValueChanges.map((item, index) => (
          <div key={`${item.providerId}_${index}`} className="mt-1">
            {item?.provider?.name} - priority changed from{" "}
            {item.initialPriority ?? item.initialValue} to{" "}
            {item.newPriority ?? item.newValue}
          </div>
        ))}
      {isBalanceReported &&
        `Correct Balance: ${parsePrice(
          centsToDollars(item.amountInCents || 0)
        )}, Provider: ${item.toProvider?.name}, Notes: ${item.description}`}
      {item.note && ` Note: ${item.note}`}
      {isTransferBlocked &&
        `Transfer Blocked${item.user?.name ? ` by ${item.user.name}` : ""}`}
      {isTransferReady &&
        `Transfer Marked Ready${
          item.user?.name ? ` by ${item.user.name}` : ""
        }`}
    </div>
  );
};

const custimizedMarker = (item: IOrderHistory) => {
  const iconClasses: { [key: string]: string } = {
    Created: "pi pi-check",
    Updated: "pi pi-pencil",
    Canceled: "pi pi-times",
    Paid: "pi pi-dollar",
    Transfer: "pi pi-sync",
    PartiallyPaid: "pi pi-dollar",
    InstallmentPaid: "pi pi-dollar",
    EmailSent: "pi pi-envelope",
    TextMessageSent: "pi pi-comment",
    EmailResent: "pi pi-envelope",
    TextMessageResent: "pi pi-comment",
    EstimateOpened: "pi pi-eye",
    NotesAdded: "pi pi-file-edit",
    NoteToPdf: "pi pi-file-edit",
    Redone: "pi pi-refresh",
    CreatedAsDraft: "pi pi-check",
    Approved: "pi pi-check",
    PaymentMarkedBlockedForTransfers: "pi pi-ban",
    PaymentMarkedReadyForTransfers: "pi pi-check",
    PaidAdditionalAmount: "pi pi-dollar",
    BalanceUpdated: "pi pi-dollar",
    BalanceReported: "pi pi-wrench",
    NextInstallmentDateChanged: "pi pi-calendar",
    CreatedWithDirectPay: "pi pi-check",
    ProviderPriorityUpdated: "pi pi-sort-numeric-down",
    CardUpdated: "pi pi-credit-card",
    ExternalPaymentRecorded: "pi pi-file-edit",
    TextMessageFailed: "pi pi-times-circle",
  };
  const isError = item.type === "TextMessageFailed";
  const markerColor = isError ? "var(--red-500)" : "var(--color-mint-5)";
  return (
    <div
      className="flex w-2rem h-2rem align-items-center justify-content-center border-2 border-circle"
      style={{
        borderColor: markerColor,
        color: markerColor,
      }}
    >
      <i className={iconClasses[item.type]} />
    </div>
  );
};

const typeRenderer: { [key: string]: string } = {
  PartiallyPaid: "Partially Paid",
  InstallmentPaid: "Auto Paid",
  EmailSent: "Email Sent",
  TextMessageSent: "Text Message Sent",
  EmailResent: "Email Resent",
  TextMessageResent: "Text Message Resent",
  EstimateOpened: "Estimate Opened",
  NotesAdded: "Note Added",
  NoteToPdf: "Note Added to PDF",
  CreatedAsDraft: "Created as Draft",
  PaymentMarkedBlockedForTransfers: "Transfer Blocked",
  PaymentMarkedReadyForTransfers: "Transfer Ready",
  PaidAdditionalAmount: "Paid Additional Amount",
  BalanceUpdated: "Balance Updated",
  BalanceReported: "Balance Reported",
  NextInstallmentDateChanged: "Next Installment Date Changed",
  CreatedWithDirectPay: "Created with Direct Pay",
  ProviderPriorityUpdated: "Provider Payment Priority Updated",
  CardUpdated: "Card updated",
  ExternalPaymentRecorded: "External Payment Recorded",
  TextMessageFailed: "Text Message Failed",
};

function OrderHistory({
  orderHistories,
  orderCreated,
  orderId,
  onUpdateOrder,
  patientName,
  orderPaymentNotesEnabled,
}: {
  orderHistories: IOrderHistory[];
  orderCreated: string;
  orderId: string;
  onUpdateOrder: () => void;
  patientName: string;
  orderPaymentNotesEnabled?: boolean;
}) {
  const [showMore, setShowMore] = useState(false);
  const [note, setNote] = useState("");
  const [dialogVisible, setDialogVisible] = useState(false);
  const [paymentData, setPaymentData] = useState<IPaymentNoteData>({
    isPaymentNote: false,
    paymentAmount: null,
    paymentType: null,
  });
  const [createOrderHistory] = useCreateOrderHistoryMutation();
  const { current: toastElement } = useContext(ToastContext);
  const { userInfo } = useUserWithAccount();
  const orderHistoryList = useMemo(() => {
    const orderHistoryListUpdated = [...orderHistories];
    const hasCreatedType = orderHistories.some(
      (item) => item.type === "Created"
    );
    const isOldOrder = new Date(orderCreated) < new Date("2024-12-01");
    if (!hasCreatedType && isOldOrder) {
      orderHistoryListUpdated.unshift({
        id: "",
        type: "Created",
        createdAt: orderCreated,
        orderId,
      });
    }
    return orderHistoryListUpdated;
  }, [orderHistories, orderId, orderCreated]);

  const onAddNote = () => {
    try {
      const { isPaymentNote, paymentAmount, paymentType } = paymentData;

      createOrderHistory({
        orderId,
        type: isPaymentNote
          ? orderHsitoryTypeConstants.ExternalPaymentRecorded
          : orderHsitoryTypeConstants.NotesAdded,
        note: note?.trim()?.length ? note.trim() : undefined,
        userId: userInfo?.id,
        ...(isPaymentNote && {
          amountInCents: paymentAmount
            ? dollarsToCents(paymentAmount)
            : undefined,
          externalPaymentMethod: paymentType as
            | "Cash"
            | "Check"
            | "CareCredit"
            | undefined,
        }),
      })
        .unwrap()
        .then(async () => {
          onUpdateOrder();
          toastElement?.show({
            summary: "Success!",
            severity: "success",
            detail: "Order Note has been added successfully.",
          });
        });
    } catch (error) {
      toastElement?.show({
        severity: "error",
        detail: "Try again later.",
        summary: "Something went wrong.",
      });
    }
    setDialogVisible(false);
    setNote("");
    setPaymentData({
      isPaymentNote: false,
      paymentAmount: null,
      paymentType: null,
    });
  };

  const header = () => {
    return (
      <div className="flex flex-column col-12 align-items-end">
        <div>
          <Button
            label="Add Note"
            icon="pi pi-plus-circle"
            onClick={() => {
              setDialogVisible(true);
            }}
            className="p-button-outlined-light"
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <AddNoteDialog
        note={note}
        setNote={setNote}
        showDialog={dialogVisible}
        setShowDialog={setDialogVisible}
        paymentData={paymentData}
        setPaymentData={setPaymentData}
        orderPaymentNotesEnabled={orderPaymentNotesEnabled}
        onAddNote={onAddNote}
      />
      <StyledCard title="Order history" header={header()}>
        <StyledTimeline
          value={showMore ? orderHistoryList : orderHistoryList.slice(0, 3)}
          content={(item) => generateOrderHistoryLine(item, patientName)}
          opposite={(item) => typeRenderer[item.type] || item.type}
          marker={(item) => custimizedMarker(item)}
        />
        {orderHistoryList?.length > 3 && (
          <StyledShowMore
            className="small-text purple flex align-items-center cursor-pointer mb-4"
            onClick={() => setShowMore(!showMore)}
          >
            {showMore ? (
              <>
                Show Less <i className="pi pi-angle-up ml-1" />
              </>
            ) : (
              <>
                Show More <i className="pi pi-angle-down ml-1" />
              </>
            )}
          </StyledShowMore>
        )}
      </StyledCard>
    </>
  );
}

export default OrderHistory;
